// src/components/digital_twin/twin_engine/ui/SimulationTab.js
// This asset's record in the active Digital Twin simulation (hurricane, rainfall run-off,
// pressure, …) — ported from the v5 overlay with readable layout.
import React, { memo } from 'react';
import { Box, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { FaCloudShowersHeavy, FaListUl } from 'react-icons/fa';
import { Empty, KV, LevelBadge, Section, Stat } from './ui';

const fmtHr = (h) => (h == null ? '—' : h < 1 ? `${Math.round(h * 60)} min` : `+${Math.round(h * 10) / 10} h`);
const STATE = { critical: 'critical', failed: 'critical', warning: 'watch', degraded: 'watch', ok: 'ok' };

function SimulationTab({ record: r, meta, downstreamCount }) {
  if (!r) {
    return <Empty icon={FaCloudShowersHeavy} title={meta ? 'Not affected by the active simulation' : 'No simulation running'}>
      {meta ? `“${meta.title}” does not reach this asset.` : 'Start a scenario (hurricane, rainfall run-off, …) from the Simulation tab of the twin sidebar.'}
    </Empty>;
  }
  const probs = r.damage_probabilities || {};
  const state = String(r.state || 'ok');
  const rows = [
    ['Scenario', meta?.title || '—'],
    r.damage_state && ['Damage state', r.damage_state],
    r.peak_wind_kts != null && ['Peak wind / gust', `${r.peak_wind_kts} / ${r.peak_gust_kts ?? '—'} kt`],
    r.peak_wind_at && ['Peak wind at', new Date(r.peak_wind_at).toLocaleString()],
    r.peak_flood_depth_m != null && ['Peak flood depth', `${r.peak_flood_depth_m} m (${r.flood_source || 'none'})`],
    r.time_to_flood_hours != null && ['Flooding', `${fmtHr(r.time_to_flood_hours)} for ${r.hours_flooded ?? '—'} h`],
    r.total_rainfall_mm != null && ['Rainfall at asset', `${r.total_rainfall_mm} mm`],
    r.peak_inflow_lps != null && ['Peak inflow', `${r.peak_inflow_lps} L/s`],
    r.capacity_lps != null && ['Effective capacity', `${r.capacity_lps} L/s`],
    r.utilisation_pct != null && ['Capacity used', `${r.utilisation_pct}%`],
    r.runoff_volume_m3 != null && ['Run-off received', `${r.runoff_volume_m3} m³ (+${r.received_overflow_m3 ?? 0} m³ overflow in)`],
    r.overflow_m3 > 0 && ['Overflow out', `${r.overflow_m3} m³${r.overflow_to ? ` → ${r.overflow_to.name || r.overflow_to.asset_id} (${r.overflow_to.distance_m} m)` : ''}`],
    r.clearance_pct != null && ['Silt clearance used', `${r.clearance_pct}%`],
    r.live_level_pct != null && ['Live level at start', `${r.live_level_pct}% full`],
    r.catchment_area_m2 != null && ['Catchment', `${r.catchment_area_m2.toLocaleString()} m²${r.terrain_is_assumed ? ' (assumed)' : ' (TessaDEM)'}`],
    r.terrain_factor != null && ['Terrain factor', r.terrain_factor],
    r.expected_repair_cost_usd != null && ['Expected repair', `$${Number(r.expected_repair_cost_usd).toLocaleString()}`],
    r.restoration_days > 0 && ['Restoration', `${r.restoration_days} d · back by day ${r.restored_by_day}`],
    r.simulated_psi != null && ['Simulated pressure', `${r.simulated_psi} psi`],
    r.cascade_from_asset_id != null && ['Impacted via', `asset ${r.cascade_from_asset_id}`],
    downstreamCount > 0 && ['Downstream impacted', String(downstreamCount)],
    r.hardening_priority && ['Hardening priority', r.hardening_priority.replace(/_/g, ' ')],
  ].filter(Boolean);
  return (
    <VStack align="stretch" spacing={4}>
      <Section title="Simulated outcome" icon={FaCloudShowersHeavy} accent right={<LevelBadge level={STATE[state] || 'watch'}>{state}</LevelBadge>}>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mb={3}>
          <Stat label="Service impact" value={`${Math.round((r.functional_impact ?? r.impact_score ?? 0) * 100)}%`} />
          {probs.moderate_or_worse != null && <Stat label="P(moderate+)" value={`${Math.round(probs.moderate_or_worse * 100)}%`} color={probs.moderate_or_worse > 0.5 ? 'red.400' : 'orange.400'} />}
          {probs.complete != null && <Stat label="P(complete)" value={`${Math.round(probs.complete * 100)}%`} color="red.400" />}
        </SimpleGrid>
        <Box>{rows.map(([k, v]) => <KV key={k} label={k} value={v} />)}</Box>
      </Section>
      {Array.isArray(r.recommended_actions) && r.recommended_actions.length > 0 && (
        <Section title="Recommended actions" icon={FaListUl}>
          {r.recommended_actions.map((a) => <Text key={a} fontSize="13px" py={1}>▸ {a}</Text>)}
        </Section>
      )}
    </VStack>
  );
}

export default memo(SimulationTab);
